import { X, Download } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * PWA Install Banner Component
 *
 * Features:
 * - Bottom banner with dismiss button
 * - Non-intrusive design
 * - Dismissable with X button
 * - "Add to Home Screen" CTA
 * - Mobile-first responsive design
 * - Auto-hide after installation
 *
 * Accessibility:
 * - ARIA labels for buttons
 * - Keyboard navigation support
 * - Focus management
 *
 * Usage:
 * ```tsx
 * const { install, dismissBanner } = usePWA();
 * <InstallBanner onInstall={install} onDismiss={dismissBanner} />
 * ```
 */

interface InstallBannerProps {
  onInstall: () => Promise<boolean>;
  onDismiss: () => void;
}

export function InstallBanner({ onInstall, onDismiss }: InstallBannerProps) {
  const handleInstall = async () => {
    const success = await onInstall();
    if (success) {
      // Banner will auto-hide when app is installed via usePWA hook
      console.log('App installation successful');
    }
  };

  return (
    <div
      role="banner"
      aria-label="Install app banner"
      className={cn(
        'fixed bottom-0 left-0 right-0',
        'z-50',
        'bg-background',
        'border-t border-border',
        'shadow-lg',
        'animate-in slide-in-from-bottom duration-300'
      )}
    >
      <div className="container mx-auto px-4 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Icon + Text */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Download className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-base font-semibold text-foreground truncate">
                Install Omokage
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">
                Quick access from your home screen
              </p>
            </div>
          </div>

          {/* Right: Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleInstall}
              aria-label="Install app"
              className={cn(
                'px-3 py-2 sm:px-4 sm:py-2',
                'bg-primary text-primary-foreground',
                'rounded-lg',
                'text-xs sm:text-sm font-medium',
                'hover:bg-primary/90',
                'transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                'whitespace-nowrap'
              )}
            >
              Install
            </button>
            <button
              onClick={onDismiss}
              aria-label="Dismiss banner"
              className={cn(
                'p-2',
                'rounded-lg',
                'text-muted-foreground',
                'hover:bg-muted',
                'transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
              )}
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Install Button Component for Settings Page
 *
 * Standalone button that can be placed anywhere in the UI.
 * Used in Settings page for permanent access to install feature.
 *
 * Usage:
 * ```tsx
 * const { canInstall, isInstalled, install } = usePWA();
 * if (canInstall && !isInstalled) {
 *   return <InstallButton onClick={install} />;
 * }
 * ```
 */

interface InstallButtonProps {
  onClick: () => Promise<boolean>;
  className?: string;
}

export function InstallButton({ onClick, className }: InstallButtonProps) {
  const handleClick = async () => {
    const success = await onClick();
    if (success) {
      console.log('App installed successfully');
    }
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        'flex items-center gap-2',
        'px-4 py-2',
        'bg-primary text-primary-foreground',
        'rounded-lg',
        'text-sm font-medium',
        'hover:bg-primary/90',
        'transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        className
      )}
    >
      <Download className="w-4 h-4" />
      Add to Home Screen
    </button>
  );
}
